import { reduce, remove, each, isFunction, isNumber } from 'lodash-es';
import numeral from 'numeral';
import type { TableModel } from './table-model';
import type { RowModel } from './row-model';
import type { RowGroupModel } from './row-group-model';
import type { IColumnDefinition } from '../types';

export class FooterTotalsRowModel {
  public readonly isFooterTotalsRow: boolean = true;

  readonly id: string;
  readonly table: TableModel;
  readonly group?: RowGroupModel;
  readonly rowKey: string;
  readonly validColumns: string[];

  public data: Record<string, number>;

  constructor(table: TableModel, group?: RowGroupModel) {
    this.id = `table__footer_totals`;
    this.table = table;
    this.group = group;

    this.rowKey = group ? `${group.id}__footer_totals` : 'table_footer_totals';
    this.validColumns = this.getValidColumns(table);
    this.data = this.calculateTotals();
  }

  getValidColumns(table: TableModel) {
    const result: string[] = [];
    for (const col of table.options.columns) {
      if (
        (col.type === 'number' || col.footerTotals) &&
        col.noFooterTotals !== true
      ) {
        result.push(col.field || col.name);
      }
    }
    return result;
  }

  public getValue(fieldName: string) {
    if (this.validColumns.indexOf(fieldName) > -1) {
      return this.data[fieldName];
    }
  }

  public displayTotalsForColumn(column: IColumnDefinition) {
    return this.validColumns.indexOf(column.field) > -1;
  }

  calculateTotals() {
    const cols = [...this.validColumns];
    const totals: Record<string, number> = {};

    const rowsToCalculate: RowModel[] = this.group
      ? this.group.rows
      : [...this.table.iterRows('')];

    for (const col of this.table.options.columns) {
      if (isFunction(col.footerTotals)) {
        totals[col.field] = col.footerTotals(
          rowsToCalculate.map((r) => r.data),
        );
        remove(cols, (c) => c === col.field);
      }
    }

    const numeralTotals = reduce(
      cols,
      (m, field) => {
        m[field] = numeral(0);
        return m;
      },
      {} as Record<string, numeral.Numeral>,
    );

    for (const row of rowsToCalculate) {
      for (const fieldName of cols) {
        const value = row.getValue(fieldName);
        if (isNumber(value)) {
          numeralTotals[fieldName].add(value);
        }
      }
    }

    each(numeralTotals, (v, k) => {
      totals[k] = v.value()!;
    });

    return totals;
  }
}
